import React, { useState } from 'react';

import { AspectRatioOutlined } from '@mui/icons-material';
import { AvatarProps, AvatarPropsDefaults, AvatarPropsSchema } from '@usewaypoint/block-avatar';
import { Image } from 'lucide-react';

import BaseSidebarPanel, { SectionHeader } from './helpers/BaseSidebarPanel';
import RadioGroupInput from './helpers/inputs/RadioGroupInput';
import SliderInput from './helpers/inputs/SliderInput';
import TextInput from './helpers/inputs/TextInput';
import MultiStylePropertyPanel from './helpers/style-inputs/MultiStylePropertyPanel';
import { Separator } from '@/components/ui/separator';
import { ToggleGroupItem } from '@/components/ui/toggle-group';

type AvatarSidebarPanelProps = {
  data: AvatarProps;
  setData: (v: AvatarProps) => void;
};

export default function AvatarSidebarPanel({ data, setData }: AvatarSidebarPanelProps) {
  const [, setErrors] = useState<Zod.ZodError | null>(null);
  const updateData = (d: unknown) => {
    const res = AvatarPropsSchema.safeParse(d);
    if (res.success) {
      setData(res.data);
      setErrors(null);
    } else {
      setErrors(res.error);
    }
  };

  const size = data.props?.size ?? AvatarPropsDefaults.size;
  const imageUrl = data.props?.imageUrl ?? AvatarPropsDefaults.imageUrl;
  const alt = data.props?.alt ?? AvatarPropsDefaults.alt;
  const shape = data.props?.shape ?? AvatarPropsDefaults.shape;

  return (
    <BaseSidebarPanel title="Avatar block">
      {/* IMAGE SECTION */}
      <div className="space-y-4">
        <SectionHeader icon={Image}>Image</SectionHeader>
        <div className="space-y-3">
          <TextInput
            label="Image URL"
            defaultValue={imageUrl}
            onChange={(imageUrl) => {
              updateData({ ...data, props: { ...data.props, imageUrl } });
            }}
          />
          <TextInput
            label="Alt text"
            defaultValue={alt}
            onChange={(alt) => {
              updateData({ ...data, props: { ...data.props, alt } });
            }}
          />
        </div>
      </div>

      <Separator className="my-6" />

      {/* APPEARANCE SECTION */}
      <div className="space-y-4">
        <SectionHeader>Appearance</SectionHeader>
        <div className="space-y-3">
          <SliderInput
            label="Size"
            iconLabel={<AspectRatioOutlined />}
            units="px"
            step={3}
            min={32}
            max={256}
            defaultValue={size}
            onChange={(size) => {
              updateData({ ...data, props: { ...data.props, size } });
            }}
          />
          <RadioGroupInput
            label="Shape"
            defaultValue={shape}
            onChange={(shape) => {
              updateData({ ...data, props: { ...data.props, shape } });
            }}
          >
            <ToggleGroupItem value="circle">Circle</ToggleGroupItem>
            <ToggleGroupItem value="square">Square</ToggleGroupItem>
            <ToggleGroupItem value="rounded">Rounded</ToggleGroupItem>
          </RadioGroupInput>
        </div>
      </div>

      <Separator className="my-6" />

      {/* LAYOUT SECTION */}
      <div className="space-y-4">
        <SectionHeader>Layout</SectionHeader>
        <div className="space-y-3">
          <MultiStylePropertyPanel
            names={['textAlign', 'padding']}
            value={data.style}
            onChange={(style) => updateData({ ...data, style })}
          />
        </div>
      </div>
    </BaseSidebarPanel>
  );
}
